/**
 * Page principale du module Séances unifié.
 * Route : /dashboard/seances?tab={seances|insightlab}
 *
 * 2 onglets depuis DATA-003 (fusion Suggérées/Mes séances/Modèles).
 * Onglet par défaut : "Séances". Anciens params ?tab=suggerees/mes-seances/modeles
 * redirigent silencieusement vers "Séances" via le fallback || 'seances'.
 */
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Lock, ArrowRight } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import { useFreeStatus } from '../hooks/useFreeStatus'
import { T } from '../theme'

import SeancesTab   from '../components/seances/SeancesTab'
import InsightlabTab from '../components/seances/InsightlabTab'


const TABS = [
  { id: 'seances',    label: 'Séances' },
  { id: 'insightlab', label: 'Procédés' },
]


export default function SeancesPage() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const { isExpired, formattedExpiresAt } = useFreeStatus()
  const tab = TABS.find(t => t.id === params.get('tab'))?.id || 'seances'

  if (isExpired) {
    return (
      <DashboardLayout>
        <div style={{
          maxWidth: 560, margin: '60px auto', padding: 40,
          textAlign: 'center',
          background: T.surface, border: `1px solid ${T.rule}`,
          borderTop: `2px solid ${T.gold}`,
        }}>
          <Lock size={32} color={T.gold} style={{ margin: '0 auto 16px' }} />
          <h2 style={{
            fontFamily: T.display, fontSize: 24, color: T.ink,
            textTransform: 'uppercase', letterSpacing: '.02em',
            margin: '0 0 12px',
          }}>
            Ton essai est terminé.
          </h2>
          <p style={{
            fontFamily: T.mono, fontSize: 12, color: T.muted2,
            lineHeight: 1.6, margin: '0 0 22px',
          }}>
            {formattedExpiresAt
              ? `Ton essai Free a pris fin le ${formattedExpiresAt}. `
              : 'Ton essai Free est terminé. '}
            Passe en Coach+ pour accéder aux séances et continuer ta saison.
          </p>
          <button
            type="button"
            onClick={() => navigate('/dashboard/settings')}
            style={{
              padding: '12px 22px',
              background: T.dark, color: '#FFFFFF', border: 'none',
              fontFamily: T.mono, fontSize: 11, fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              borderRadius: T.radius, minHeight: 44,
            }}
          >
            Passer en Coach+ <ArrowRight size={13} />
          </button>
        </div>
      </DashboardLayout>
    )
  }

  const setTab = (id) => {
    const next = new URLSearchParams(params)
    next.set('tab', id)
    // Purge des params spécifiques à chaque onglet au switch
    if (id !== 'insightlab') {
      ['labProcede', 'labSearch', 'labView', 'labPage', 'labTheme'].forEach(k => next.delete(k))
    }
    if (id !== 'seances') {
      ['seancesTheme', 'seancesSource', 'seancesStatus', 'seancesPage'].forEach(k => next.delete(k))
    }
    setParams(next, { replace: false })
  }

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 24px 60px', maxWidth: 1280, margin: '0 auto' }}>
        {/* Header module */}
        <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `0.5px solid ${T.rule}` }}>
          <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
            Séances
          </p>
          <h1 style={{
            fontFamily: T.display, fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: 800, color: T.ink,
            textTransform: 'uppercase', letterSpacing: '-.01em',
            lineHeight: 1, margin: 0,
          }}>
            Préparer ma séance.
          </h1>
        </div>

        {/* Onglets */}
        <div
          role="tablist"
          aria-label="Onglets Séances"
          style={{
            display: 'flex', gap: 0, marginBottom: 20,
            borderBottom: `0.5px solid ${T.rule}`,
            overflowX: 'auto',
          }}
        >
          {TABS.map(t => {
            const active = t.id === tab
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  padding: '10px 18px',
                  border: 'none',
                  background: 'transparent',
                  color: active ? T.ink : T.muted,
                  fontFamily: T.mono, fontSize: 12, fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  borderBottom: active ? `2px solid ${T.ink}` : '2px solid transparent',
                  marginBottom: -1,
                  whiteSpace: 'nowrap',
                }}
              >
                {t.label}
              </button>
            )
          })}
        </div>

        {/* Contenu */}
        <div role="tabpanel">
          {tab === 'seances'    && <SeancesTab />}
          {tab === 'insightlab' && <InsightlabTab />}
        </div>
      </div>
    </DashboardLayout>
  )
}
